import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import { SEOHead, buildBreadcrumbSchema } from "../components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }) };

const DEFAULT_SECTIONS = [
  { heading: "Acceptance of Terms", body: "By accessing or using Tripsi services, you agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use of our services immediately." },
  { heading: "Service Description", body: "Tripsi provides a technology platform that connects riders with independent drivers. Tripsi does not provide transportation services directly. Drivers are independent contractors, not employees of Tripsi." },
  { heading: "User Accounts", body: "You must provide accurate, current information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account." },
  { heading: "Rider Responsibilities", body: "Riders must be at least 18 years old, provide accurate pickup and destination information, treat drivers with respect, and comply with all applicable local laws during their ride." },
  { heading: "Driver Responsibilities", body: "Drivers must maintain valid licenses and insurance, keep vehicles in safe condition, comply with all traffic laws, and provide professional service to all riders." },
  { heading: "Payments & Fees", body: "Fares are calculated based on distance, time, and applicable rates. Payment is processed through the app. Tripsi may charge service fees. All fares are final unless a dispute is raised within 48 hours." },
  { heading: "Cancellation Policy", body: "Riders may cancel a ride before the driver arrives. Cancellation fees may apply if cancelled after the driver has accepted and is en route. Drivers must accept rides in good faith." },
  { heading: "Safety & Conduct", body: "All users must follow safety guidelines. Discrimination, harassment, and illegal activity are strictly prohibited. Tripsi reserves the right to suspend accounts that violate community standards." },
  { heading: "Limitation of Liability", body: "Tripsi is not liable for indirect, incidental, or consequential damages. Our total liability is limited to the amount paid for the specific service in question." },
  { heading: "Governing Law", body: "These terms are governed by the laws of Canada and the province in which the service is provided. Disputes shall be resolved through binding arbitration." },
  { heading: "Contact", body: "For questions about these terms, contact us at support@tripsi.ca or call +1 647-249-9977." },
];

export default function TermsPage() {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetch(`${API}/cms/terms`).then(r => r.json()).then(data => {
      if (data.content?.sections?.length) {
        setSections(data.content.sections);
      }
      if (data.content?.updated_at) setLastUpdated(data.content.updated_at);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="terms-page" className="min-h-screen">
      <SEOHead
        title="Terms & Conditions"
        description="Tripsi's terms and conditions. Read our terms of service for riders, drivers, and business partners using Tripsi transportation services."
        canonical="/terms"
        jsonLd={buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Terms & Conditions", url: "/terms" }])}
      />
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E60000]/5 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 text-center">
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0} className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-6">
              <FileText size={24} className="text-[#E60000]" />
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-4">Terms & Conditions</motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-[#A3A3A3] text-base md:text-lg max-w-xl mx-auto">Please read these terms carefully before using Tripsi services.</motion.p>
            {lastUpdated && <motion.p variants={fadeUp} custom={3} className="text-[#A3A3A3]/60 text-xs mt-4">Last updated: {new Date(lastUpdated).toLocaleDateString()}</motion.p>}
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-8 space-y-8">
          {sections.map((sec, i) => (
            <motion.div key={sec.heading || `section-${i}`} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i % 3} className="bg-[#141414] border border-white/5 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <span className="font-['Outfit'] text-2xl font-black text-[#E60000]/15 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-['Outfit'] text-lg md:text-xl font-bold text-white mb-3">{sec.heading}</h2>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-line">{sec.body}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
